// R-162 §10.1 / WU-3.8 — /admin/queue-blocks list page.
//
// Lists active + last-30d cleared queue blocks for the tenant. A supervisor
// can create a new block (CreateBlockDialog) or clear an active one. A
// duplicate-active block surfaces the WU-3A 409 `queue_already_blocked` as a
// friendly toast (an active block already exists for that step + job type).
import { useMemo, useState } from "react";
import { AdminListHeader } from "@/components/admin/AdminListHeader";
import { showApiError } from "@/lib/error-toast";
import { formatTimestamp } from "@/lib/formatters";
import { useJobTypes } from "@/features/admin/hooks/useJobTypes";
import { useQueueBlocks } from "../hooks/useQueueBlocks";
import { queueBlocksApi, type CreateQueueBlockPayload } from "../api/client";
import { CreateBlockDialog } from "../components/CreateBlockDialog";
import styles from "../queue-blocks.module.css";

const formatQueueBlockError = (body: unknown): string | undefined => {
  if (
    body &&
    typeof body === "object" &&
    (body as { code?: string }).code === "queue_already_blocked"
  ) {
    return "An active block already exists for this step + job type.";
  }
  return undefined;
};

export function QueueBlocksListPage() {
  const { blocks, loading, refetch } = useQueueBlocks();
  const { items: jobTypes } = useJobTypes();
  const [showCreate, setShowCreate] = useState(false);

  // Resolve jobTypeId → readable name for the table; fall back to the raw id.
  const jobTypeName = useMemo(() => {
    const map = new Map(jobTypes.map((jt) => [jt.id, jt.name]));
    return (id: string) => map.get(id) ?? id;
  }, [jobTypes]);

  const jobTypeOptions = jobTypes.map((jt) => ({ id: jt.id, name: jt.name }));

  async function handleCreate(payload: CreateQueueBlockPayload) {
    try {
      await queueBlocksApi.create(payload);
      setShowCreate(false);
      await refetch();
    } catch (e) {
      showApiError(e, { formatBody: formatQueueBlockError });
    }
  }

  async function handleClear(id: string) {
    if (!window.confirm("Clear this queue block? Operators can feed again.")) {
      return;
    }
    try {
      await queueBlocksApi.delete(id);
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  }

  return (
    <div className={styles.listPage}>
      <AdminListHeader
        title="Queue Blocks"
        count={blocks.length}
        primaryAction={{
          label: "Block queue",
          onClick: () => setShowCreate(true),
        }}
      />

      {loading ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : blocks.length === 0 ? (
        <div className={styles.emptyState}>
          No queue blocks. Parking a lane stops operators from feeding it until
          you clear the block.{" "}
          <button
            type="button"
            className={styles.primary}
            style={{ marginLeft: 12 }}
            onClick={() => setShowCreate(true)}
          >
            Block queue
          </button>
        </div>
      ) : (
        <table className={styles.blockTable}>
          <thead>
            <tr>
              <th>Step</th>
              <th>Job Type</th>
              <th>Reason</th>
              <th>Blocked By</th>
              <th>Blocked At</th>
              <th>Cleared At</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blocks.map((b) => {
              const active = b.clearedAt === null;
              return (
                <tr key={b.id}>
                  <td>
                    <code>{b.stepType}</code>
                  </td>
                  <td>{jobTypeName(b.jobTypeId)}</td>
                  <td className={styles.reasonCell} title={b.reason}>
                    {b.reason}
                  </td>
                  <td>{b.blockedBy}</td>
                  <td>{formatTimestamp(b.blockedAt)}</td>
                  <td>{formatTimestamp(b.clearedAt)}</td>
                  <td>
                    {active ? (
                      <span className={styles.activeBadge}>● Active</span>
                    ) : (
                      <span className={styles.clearedBadge}>Cleared</span>
                    )}
                  </td>
                  <td>
                    {active && (
                      <div className={styles.rowActions}>
                        <button type="button" onClick={() => handleClear(b.id)}>
                          Clear
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showCreate && (
        <CreateBlockDialog
          jobTypes={jobTypeOptions}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}
    </div>
  );
}

export default QueueBlocksListPage;
